import { ConditionalWrap, Label, SpotlightTarget } from '@neo4j-ndl/react';
import { ArrowUpRightIconOutline } from '@neo4j-ndl/react/icons';
import { SideNavigation } from '@neo4j-ndl/react/next';
import type { ComponentPropsWithoutRef } from 'react';
import { NavLink, useLocation } from 'react-router';

import type { LinkMenuItemTag } from '../left-nav-menu/left-nav-menu';

type SpotlightTargetProps = {
  spotlightId?: string;
};

type SideNavLinkItem = {
  type: 'link-item';
  icon?: React.ReactNode;
  label: string;
  route: string;
  tag?: LinkMenuItemTag;
  rightIcon?: React.ReactNode;
  onClick?: () => void;
} & SpotlightTargetProps;

type SideNavCategoryItem = {
  type: 'category-item';
  icon?: React.ReactNode;
  label: string;
  items: SideNavLinkItem[];
  isMenuOpen?: boolean;
} & SpotlightTargetProps;

type SideNavGroupHeaderItem = {
  type: 'category-header';
  label: string;
} & SpotlightTargetProps;

type SideNavListItem = SideNavLinkItem | SideNavCategoryItem;

type SideNavChild =
  | {
      type: 'list';
      items: (SideNavListItem | undefined)[];
    }
  | {
      type: 'divider';
    }
  | SideNavGroupHeaderItem;

export type SideNavProps = {
  items: (SideNavChild | undefined)[];
} & ComponentPropsWithoutRef<typeof SideNavigation>;

type ConditionalSpotlightTargetProps = {
  item: SideNavListItem | SideNavGroupHeaderItem;
  children: React.ReactNode;
};

const ConditionalSpotlightTarget = ({ item, children }: ConditionalSpotlightTargetProps) => {
  return (
    <ConditionalWrap
      shouldWrap={item.spotlightId !== undefined}
      wrap={(child) => (
        <SpotlightTarget
          id={item.spotlightId ?? ''}
          indicatorVariant="point"
          htmlAttributes={{ 'aria-label': undefined }}
        >
          {child}
        </SpotlightTarget>
      )}
    >
      {children}
    </ConditionalWrap>
  );
};

const NavItem = ({ item: navItem, isActive }: { item: SideNavLinkItem; isActive: boolean }) => {
  if (navItem.route.startsWith('http')) {
    return (
      <ConditionalSpotlightTarget item={navItem}>
        <SideNavigation.NavItem
          as="a"
          label={navItem.label}
          href={navItem.route}
          htmlAttributes={{ rel: 'noreferrer', target: '_blank', onClick: navItem.onClick }}
          rightElement={<ArrowUpRightIconOutline className="text-palette-neutral-icon size-4" />}
          className="hover:underline"
        />
      </ConditionalSpotlightTarget>
    );
  }

  return (
    <ConditionalSpotlightTarget item={navItem}>
      <SideNavigation.NavItem
        as={NavLink}
        label={navItem.label}
        icon={navItem.icon}
        isSelected={isActive}
        rightElement={
          <>
            {navItem.tag && (
              <Label color="info" className="h-4 px-1.5">
                {navItem.tag}
              </Label>
            )}
            {Boolean(navItem.rightIcon) && <div className="flex-shrink-0">{navItem.rightIcon}</div>}
          </>
        }
        htmlAttributes={{
          'aria-current': isActive ? 'page' : undefined,
          to: navItem.route,
          onClick: navItem.onClick,
        }}
      />
    </ConditionalSpotlightTarget>
  );
};

const CategoryItem = ({ item: categoryItem, currentPath }: { item: SideNavCategoryItem; currentPath: string }) => {
  const isActive = categoryItem.items.some((item) => currentPath.startsWith(item.route));

  return (
    <ConditionalSpotlightTarget item={categoryItem}>
      <SideNavigation.CategoryItem
        label={categoryItem.label}
        icon={categoryItem.icon}
        isSelected={isActive}
        isMenuOpen={categoryItem.isMenuOpen}
      >
        {categoryItem.items.map((navItem) => (
          <NavItem key={navItem.label} item={navItem} isActive={currentPath.startsWith(navItem.route)} />
        ))}
      </SideNavigation.CategoryItem>
    </ConditionalSpotlightTarget>
  );
};

const CategoryHeaderItem = ({ item: categoryHeaderItem }: { item: SideNavGroupHeaderItem }) => {
  return (
    <ConditionalSpotlightTarget item={categoryHeaderItem}>
      <SideNavigation.CategoryHeader>{categoryHeaderItem.label}</SideNavigation.CategoryHeader>
    </ConditionalSpotlightTarget>
  );
};

export function SideNav({ items, ...restProps }: SideNavProps) {
  const location = useLocation();

  return (
    <SideNavigation {...restProps}>
      {items.map((item, index) => {
        if (!item) {
          return null;
        }

        switch (item.type) {
          case 'list':
            return (
              <SideNavigation.List key={`nav-list-${index}`}>
                {item.items.map((listItem) => {
                  if (!listItem) {
                    return null;
                  }

                  switch (listItem.type) {
                    case 'link-item':
                      return (
                        <NavItem
                          key={listItem.label}
                          item={listItem}
                          isActive={location.pathname.startsWith(listItem.route)}
                        />
                      );
                    case 'category-item':
                      return <CategoryItem key={listItem.label} item={listItem} currentPath={location.pathname} />;
                    default:
                      return null;
                  }
                })}
              </SideNavigation.List>
            );
          case 'category-header':
            return <CategoryHeaderItem key={`nav-category-header-${index}`} item={item} />;
          case 'divider':
            return <SideNavigation.Divider key={`nav-divider-${index}`} />;
          default:
            return null;
        }
      })}
    </SideNavigation>
  );
}

const link: (props: Omit<SideNavLinkItem, 'type'>) => SideNavLinkItem = ({
  label,
  route,
  icon,
  spotlightId,
  onClick,
  tag,
  rightIcon,
}: Omit<SideNavLinkItem, 'type'>) => ({
  type: 'link-item',
  label,
  route,
  icon,
  spotlightId,
  onClick,
  tag,
  rightIcon,
});

const category: (props: Omit<SideNavCategoryItem, 'type'>) => SideNavCategoryItem = ({
  label,
  items,
  icon,
  spotlightId,
  isMenuOpen,
}: Omit<SideNavCategoryItem, 'type'>) => ({
  type: 'category-item',
  label,
  items,
  icon,
  spotlightId,
  isMenuOpen,
});

const heading: (props: Omit<SideNavGroupHeaderItem, 'type'>) => SideNavChild = ({ label, spotlightId }) => ({
  type: 'category-header',
  label,
  spotlightId,
});

const list: (items: (SideNavListItem | undefined)[]) => SideNavChild = (items) => ({
  type: 'list',
  items,
});

const divider: () => SideNavChild = () => ({
  type: 'divider',
});

export const NavItems = {
  link,
  category,
  heading,
  list,
  divider,
};
